import { useLazyQuery, useMutation } from "@apollo/client/react";
import useApplicationContext from "hooks/apollo/applicationContext/useApplicationContext";
import useGetAllLines from "hooks/apollo/line/useGetLines";
import { PublishSchedule, PublishScheduleDryRun } from "hooks/apollo/publishSchedule.gql";
import usePublishScheduleModal from "hooks/state/usePublishScheduleModal";
import usePublishSummaryModal from "hooks/state/usePublishSummaryModal";
import useTimeZone from "hooks/useTimeZone";
import React, { useEffect, useState } from "react";
import PublishScheduleFormView from "./PublishScheduleFormView";

const DEFAULT_RESPONSE_STATE = { errors: [], success: null };
const DEFAULT_FILTERS_STATE = {
  dateRange: { endDate: null, startDate: null },
  linesToPublish: [],
  lineTypesToPublish: [],
};
const DEFAULT_COUNTS_STATE = { onInactiveLines: 0, toCreate: 0, total: 0, toUpdate: 0 };

export default function PublishScheduleFormContainer() {
  const { overrideToSiteTimeZone } = useTimeZone();
  const applicationContext = useApplicationContext();
  const { lines } = useGetAllLines({ activeOnly: true });

  const [response, setResponse] = useState(DEFAULT_RESPONSE_STATE);
  const resetResponse = () => setResponse(DEFAULT_RESPONSE_STATE);
  const [filters, setFilters] = useState(DEFAULT_FILTERS_STATE);
  const [jobCounts, setJobCounts] = useState(DEFAULT_COUNTS_STATE);
  const { open: openSummaryModal } = usePublishSummaryModal();
  const { close: closePublishModal } = usePublishScheduleModal();

  const {
    dateRange: { startDate, endDate },
  } = filters;

  const [publishSchedule, { loading: schedulePublishing }] = useMutation(PublishSchedule);
  const [getJobCounts, { loading: dryRunLoading }] = useLazyQuery(PublishScheduleDryRun, {
    fetchPolicy: "network-only",
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: only care about filters changing
  useEffect(() => {
    if (startDate && endDate)
      getJobCounts({
        variables: publishScheduleInformation(filters, applicationContext, overrideToSiteTimeZone),
      }).then((result) => {
        const {
          publishScheduleDryRun: {
            count: total,
            jobsToCreateCount: toCreate,
            jobsToUpdateCount: toUpdate,
            blocksOnInactiveLinesCount: onInactiveLines,
          },
        } = result.data;
        setJobCounts({ onInactiveLines, toCreate, total, toUpdate });
      });
  }, [filters]);

  const showJobCount = !dryRunLoading && startDate && endDate;

  const onSave = () => {
    resetResponse();

    return publishSchedule({
      update: (_proxy, { data }) => {
        const publishResponse = data.publishSchedule.publishedSchedule;
        const configurationErrors = publishResponse.errors.filter((error) => error.path.length !== 3);
        if (configurationErrors.length) {
          setResponse(publishResponse);
        } else {
          closePublishModal();
          openSummaryModal(publishResponse);
        }
      },
      variables: publishScheduleInformation(filters, applicationContext, overrideToSiteTimeZone),
    });
  };

  const handleFilterChange = (updatedFields) => {
    resetResponse();
    setFilters((prevState) => ({ ...prevState, ...updatedFields }));
  };

  return (
    <PublishScheduleFormView
      filters={filters}
      jobCounts={jobCounts}
      lines={lines}
      onFilterChange={handleFilterChange}
      onSave={onSave}
      response={response}
      schedulePublishing={schedulePublishing || dryRunLoading}
      showJobCount={showJobCount}
    />
  );
}

function publishScheduleInformation(
  { dateRange: { startDate, endDate }, linesToPublish: lineIds, lineTypesToPublish: lineTypes },
  _applicationContext,
  overrideToSiteTimeZone,
) {
  return {
    endsAt: overrideToSiteTimeZone(endDate),
    lineIds,
    lineTypes,
    startsAt: overrideToSiteTimeZone(startDate),
  };
}
